/**
 * Frontend entry point.
 *
 * Routing: History API, clean URLs — /login, /register, /forgot-password,
 * /reset-password?token=..., /verify-email?token=... (unauthenticated), four public content
 * pages that render in either auth state (/faq, /news, /news/:slug, /guide — the landing's
 * navigation reaches them, and a signed-in player can too), and nine authenticated
 * meta routes — /play, /loadout, /armoury, /shop, /inventory, /career, /leaderboards, /quests,
 * /settings (`engine/shell/routes.ts`) — that all share one persistent shell
 * (`engine/shell/shell.ts`): a header and nav that mount once and never rebuild while
 * navigation moves between them. `/` and any unrecognised authenticated path resolve to
 * `/play`. `/admin` is the operator dashboard (`engine/admin/admin-page.ts`) — ADMIN and
 * KYC_ADMIN only, standalone rather than inside the shell, loaded as its own chunk.
 */
import { bootstrapSession, logout } from './core/auth/auth-api';
import { renderLogin } from './core/auth/login-screen';
import { renderRegister } from './core/auth/register-screen';
import { renderForgotPassword } from './core/auth/forgot-password-screen';
import { renderResetPassword } from './core/auth/reset-password-screen';
import { renderVerifyEmail } from './core/auth/verify-email-screen';
import { renderCheckEmail } from './core/auth/check-email-screen';
import { authStore, type AuthUser } from './core/state/auth-store';
import { connectSocket, disconnectSocket } from './core/network/socket';
import { fetchSiteInfo, isPreviewBuild, stripBrandMeta } from './core/preview/preview';
import { initAnalytics, trackPageView } from './core/analytics/gtag';
import { mountShell, type ShellHandle } from './engine/shell/shell';
import { isMetaRoute, resolveMetaPath } from './engine/shell/routes';
import { mountMenuMusic, type MenuMusicHandle } from './engine/shell/menu-music';
import { renderLanding, preloadLandingScene, type LandingHandle } from './engine/landing/landing';
import { renderFaqPage } from './engine/landing/faq-page';
import { renderNewsPage, renderArticlePage } from './engine/landing/news-page';
import { renderGuidePage } from './engine/landing/guide-page';
import { decorateAuthScreen } from './engine/auth/auth-skin';
import { initI18n, subscribe as onLocaleChange } from './engine/i18n';
import { hasLocaleChoice } from './engine/i18n/locales';
import { mountLangPick } from './engine/i18n/lang-pick';
import { mountSoundToggle } from './engine/shell/sound-toggle';
import { BootProgress } from './engine/arena/boot-loading';
import { BOOT_SPLASH_STAGES, bootStageLabel, takeBootSplash } from './engine/boot-splash';

const app = document.getElementById('app')!;
let teardown: (() => void) | null = null;

/**
 * The shell mounts its own `menu-music.ts` instance once a player is signed in
 * (`shell.ts:614`), but a visitor spends real time on the six auth screens first — all of
 * which already share the shell's keyart backdrop (`bg-auth-gradient`/`bg-auth-card`) — and
 * previously heard nothing there. `mountMenuMusic()` already arms itself on the first
 * `pointerdown`/`keydown` and no-ops without an `Audio` constructor, so this is only ever
 * "start the same bed early"; `ensureAuthMusic` is idempotent (never a second concurrent
 * instance) and `stopAuthMusic` hands off the moment the shell is about to mount its own,
 * so exactly one `<audio>` element ever plays at a time.
 */
let authMusic: MenuMusicHandle | null = null;
function ensureAuthMusic(): void {
  authMusic ??= mountMenuMusic();
}
function stopAuthMusic(): void {
  authMusic?.dispose();
  authMusic = null;
}

// The shell (header + nav) is mounted once and outlives navigation between the nine meta
// routes it hosts — only its outlet swaps. It is a *second* thing `render()` can have mounted
// alongside `teardown`, not a second router: `activeShell` is null whenever the current page
// isn't one of the meta routes, and `teardown` always disposes whatever is actually mounted
// (the shell included), so the "every route returns a disposer, switching disposes the
// previous one" rule still holds for the page as a whole.
let activeShell: ShellHandle | null = null;

export function navigate(to: string) {
  if (location.pathname !== to) history.pushState(null, '', to);
  render();
}

/**
 * Mounts the shell on first arrival at a meta route and reuses it for every subsequent one —
 * only `ShellHandle.setRoute` runs on navigation between them, so the header and nav are never
 * rebuilt. `teardown` is set to the shell's own disposer, so it still fully unmounts (and
 * `activeShell` is cleared) the moment navigation leaves the group.
 */
function mountOrUpdateShell(user: AuthUser, resolvedPath: string) {
  if (!activeShell) {
    // **Dispose whatever was mounted first.** This branch used to overwrite `teardown` without
    // running it, on the reading that `app.replaceChildren()` inside `mountShell` cleans up
    // anyway. It does not clean up anything outside `app` — and `decorateAuthScreen` appends
    // its back plate and its sound switch to `<body>`, `position: fixed`. So a successful login
    // left both pinned over the shell for the rest of the session: the plate sat at top-left
    // across PLAY and LOADOUT, the switch at top-right across sign-out. Reported as a broken
    // navbar, measured as a 38px overlap on LOADOUT and 24px on sign-out, and it was never the
    // navbar. Moving between the nine meta routes still does not run this — `activeShell` is
    // already set by then, which is the whole point of the guard.
    teardown?.();
    teardown = null;
    // Hand off before the shell mounts its own bed (`shell.ts:614`) — never two players.
    stopAuthMusic();
    activeShell = mountShell(app, user, navigate, () => {
      logout();
      disconnectSocket();
      activeShell = null;
      // Sign out lands on the landing page, not the login form — the door out of the game
      // should open onto the front of the house.
      navigate('/');
    });
    teardown = () => {
      activeShell?.dispose();
      activeShell = null;
    };
  }
  activeShell.setRoute(resolvedPath);
}

/**
 * Fills the control seats the landing chrome reserves: the language picker and the sound switch.
 *
 * `landingTopBar` reserves `[data-slot="lang-pick"]`/`[data-slot="sound-toggle"]` in the bar and
 * the `-mobile` pair in the drawer but fills none of them, because it returns an element rather
 * than a disposer and both widgets hold listeners that have to be released (a document-level
 * pointer listener for the picker, `bl:settings-changed` for the toggle). The router already
 * composes disposers, so the mount belongs here — one call covers the landing and all four
 * content pages, which share that bar.
 *
 * A single enabled locale means no picker at all: a chooser with one option is furniture. The
 * sound toggle has no such condition — there is always exactly one thing it can do.
 */
function mountBarTools(root: HTMLElement): () => void {
  const disposers: Array<() => void> = [];
  const fill = (
    selector: string,
    mount: (host: HTMLElement) => () => void,
  ): void => {
    const host = root.querySelector<HTMLElement>(selector);
    if (host) disposers.push(mount(host));
  };
  // `bare` in the bar, plates in the drawer. The bar's seats sit in a line of flat nav text,
  // where a rendered steel plate reads as a block bolted on; the drawer's rows are a list of
  // controls, which is what a plate is for.
  fill('[data-slot="sound-toggle"]', (host) => mountSoundToggle(host, { bare: true }));
  fill('[data-slot="sound-toggle-mobile"]', (host) => mountSoundToggle(host, { compact: true }));
  if (hasLocaleChoice) {
    fill('[data-slot="lang-pick"]', (host) => mountLangPick(host, { bare: true }));
    fill('[data-slot="lang-pick-mobile"]', (host) => mountLangPick(host, { compact: true }));
  }
  return () => { for (const dispose of disposers) dispose(); };
}

/** Runs several disposers as one. The auth screens are core's markup plus our decoration. */
function combine(...fns: Array<(() => void) | null | undefined>): () => void {
  return () => { for (const fn of fns) fn?.(); };
}

/**
 * The four public content pages.
 *
 * Separate from both the auth screens and the nine meta routes because they are the only URLs
 * that mean the same thing signed in or out: the landing navigation links to them, and so does
 * the footer a player sees from inside the game. They mount over whatever was there (the shell
 * included), which is why the branch that serves them tears the shell down explicitly.
 */
type ContentRenderer = (app: HTMLElement, navigate: (to: string) => void) => () => void;
const CONTENT_ROUTES: Record<string, ContentRenderer> = {
  '/faq': renderFaqPage,
  '/news': renderNewsPage,
  '/guide': renderGuidePage,
};

/**
 * Mounts the route for the current URL.
 *
 * Returns the landing's handle when that is what it mounted, and null otherwise — every other
 * route is ready the moment it is on screen. The handle rather than just its `ready` promise,
 * because the boot needs a second thing from it: if the hero does not arrive in time, whoever
 * owns that timeout has to put the still in place before revealing the page.
 *
 * Only the boot path reads the return value; `navigate()` and `popstate` ignore it, because by
 * then there is already a page on screen to wait on.
 */
function render(): LandingHandle | null {
  const path = location.pathname;
  const params = new URLSearchParams(location.search);
  const user = authStore.get().user;

  const afterAuth = (): void => {
    const u = authStore.get().user;
    if (u && !u.emailVerified) { navigate('/check-email'); return; }
    connectSocket();
    navigate('/play');
  };

  // The nine authenticated meta routes (Play, Loadout, Armoury, Shop, Inventory, Career,
  // Leaderboards, Quests, Settings) share one persistent shell. This runs before the switch
  // below so that moving between them never disposes/remounts the header and nav — only the
  // switch's other branches (auth screens) do a full teardown.
  // A signed-out visitor at `/` gets the landing page — the one public screen — instead of
  // being bounced to the login form. Signed-out on a meta route still goes to login: those
  // URLs only mean something with an account behind them.
  // Public content pages, before the meta-route branch: they are not meta routes, so without
  // this they would fall through to the switch's default and bounce a visitor to /play or /.
  const contentRenderer = CONTENT_ROUTES[path];
  const articleSlug = path.startsWith('/news/') ? decodeURIComponent(path.slice('/news/'.length)) : '';
  if (contentRenderer || articleSlug) {
    teardown?.();
    teardown = null;
    activeShell = null;
    stopAuthMusic();
    const page = contentRenderer
      ? contentRenderer(app, navigate)
      : renderArticlePage(app, navigate, articleSlug);
    teardown = combine(page, mountBarTools(app));
    return null;
  }

  if (path === '/' && !user) {
    teardown?.();
    teardown = null;
    activeShell = null;
    stopAuthMusic();
    const landing = renderLanding(app, navigate);
    teardown = combine(() => landing.dispose(), mountBarTools(app));
    return landing;
  }

  if (path === '/' || isMetaRoute(path)) {
    if (!user) { teardown?.(); teardown = null; activeShell = null; navigate('/login'); return null; }
    if (!user.emailVerified) { teardown?.(); teardown = null; activeShell = null; navigate('/check-email'); return null; }

    const resolved = resolveMetaPath(path);
    if (resolved !== path) { navigate(resolved); return null; }

    connectSocket();
    mountOrUpdateShell(user, resolved);
    return null;
  }

  // Not a meta route: whatever was mounted (an auth screen, or the shell) must go before one
  // of the cases below mounts something new into `app`.
  teardown?.();
  teardown = null;
  activeShell = null;

  switch (path) {
    case '/login':
      if (user) { navigate(user.emailVerified ? '/play' : '/check-email'); return null; }
      ensureAuthMusic();
      teardown = combine(renderLogin(app, afterAuth), decorateAuthScreen(app, navigate));
      break;
    case '/register':
      if (user) { navigate(user.emailVerified ? '/play' : '/check-email'); return null; }
      ensureAuthMusic();
      teardown = combine(renderRegister(app, afterAuth), decorateAuthScreen(app, navigate));
      break;
    case '/forgot-password':
      if (user) { navigate('/play'); return null; }
      ensureAuthMusic();
      teardown = combine(renderForgotPassword(app), decorateAuthScreen(app, navigate));
      break;
    case '/reset-password':
      ensureAuthMusic();
      // Arrived from an email link: there is no landing page behind this tab to go back to.
      teardown = combine(
        renderResetPassword(app, params.get('token') ?? '', () => navigate('/login')),
        decorateAuthScreen(app, navigate, { back: false }),
      );
      break;
    case '/verify-email':
      ensureAuthMusic();
      teardown = combine(renderVerifyEmail(app, params.get('token') ?? ''), decorateAuthScreen(app, navigate, { back: false }));
      break;
    case '/admin': {
      // The operator dashboard. Standalone (no 3D shell), its own chunk (a player never pays
      // for it), and gated on role here as well as on every endpoint it calls: a PLAYER who
      // types the URL lands back on Play rather than on a page of 403s.
      if (!user) { navigate('/login'); return null; }
      // Same bounce every meta route applies: an unverified account has nowhere to go but the
      // check-your-email screen, and the role does not exempt it.
      if (!user.emailVerified) { navigate('/check-email'); return null; }
      if (user.role !== 'ADMIN' && user.role !== 'KYC_ADMIN') { navigate('/play'); return null; }
      stopAuthMusic();
      let cancelled = false;
      let disposeAdmin: (() => void) | null = null;
      import('./engine/admin/admin-page')
        .then(({ mountAdmin }) => {
          if (cancelled) return;
          disposeAdmin = mountAdmin(app, user, navigate);
        })
        .catch((err: unknown) => {
          // A chunk that fails to load (a dependency missing from a stale dev container, a dead
          // asset host) must say so — a blank page here was measured to look exactly like a
          // routing bug, and the console was the only place the truth landed.
          console.error('[admin] failed to load the dashboard chunk', err);
          if (cancelled) return;
          app.replaceChildren();
          const note = document.createElement('p');
          note.className = 'p-6 text-sm text-error';
          note.textContent = 'The admin dashboard failed to load. Check the console and reload.';
          app.append(note);
        });
      teardown = () => {
        cancelled = true;
        disposeAdmin?.();
        disposeAdmin = null;
      };
      break;
    }
    case '/check-email':
      if (!user) { navigate('/login'); return null; }
      ensureAuthMusic();
      // A half-signed-in account: "back" would drop them onto marketing mid-verification.
      teardown = combine(
        renderCheckEmail(app, user.email, () => { disconnectSocket(); navigate('/login'); }),
        decorateAuthScreen(app, navigate, { back: false }),
      );
      break;
    default:
      // Unknown path: an authenticated player lands back on Play; a signed-out visitor lands
      // on the landing page.
      navigate(user ? '/play' : '/');
      return null;
  }

  trackPageView(path + location.search);
  return null;
}

/**
 * A language change repaints by remounting, not by patching text in place.
 *
 * `t()` is read once when a node is built, so a persistent surface keeps whatever tongue it was
 * built in — and the shell is the most persistent thing in the app: it mounts once and survives
 * navigation between all nine meta routes by design. Rebuilding the whole page is the only
 * repaint that cannot miss a string, and it costs one mount on an action a player takes about
 * once ever. The shell is disposed explicitly first, because `render()` would otherwise reuse
 * the instance it already has and only swap its outlet.
 */
function remountForLocale(): void {
  teardown?.();
  teardown = null;
  activeShell = null;
  render();
}

window.addEventListener('popstate', render);

document.addEventListener('click', (e) => {
  const a = (e.target as HTMLElement).closest('a[href]') as HTMLAnchorElement | null;
  // Only an http(s) link on this origin is a route of the SPA. `mailto:` and friends have an
  // opaque origin that some browsers serialise as '' rather than 'null', which would route the
  // address as a path and leave a black page.
  if (!a || a.target || !/^https?:$/.test(a.protocol) || a.origin !== location.origin) return;
  e.preventDefault();
  navigate(a.pathname + a.search);
});

/**
 * How long the boot holds the page back on `/` waiting for the hero's first rendered frame.
 *
 * The landing waits for its live hero on purpose. Measured on the production bundle, warm cache,
 * local machine: the DOM is painted at 150 ms and the hero's first frame lands around 10 s, with
 * the main thread blocked for about 35 s in total across 40-odd long tasks (worst 2.9 s) while
 * Three, an HDRI, the map's PBR textures and five rigged GLBs are parsed and the scene is built.
 * Revealing the page before that means a visitor scrolls a stuttering page and watches the
 * backdrop assemble itself, so the loading screen covers all of it and the landing opens ready.
 *
 * `HERO_EXPECTED_MS` is what the progress bar paces itself against — the measured figure, not
 * the cap, so the bar reflects a normal load rather than crawling to a worst case.
 *
 * The cap is a backstop and nothing else: it exists so a scene that will never render (a broken
 * GLB, a dead asset host, a driver that loses the context) cannot strand somebody on a loading
 * screen for ever. Past it the page is revealed with the rendered still in the hero, which is a
 * screenshot of this very scene, so the fallback loses motion and nothing else.
 *
 * **Two things this cap cannot do, both measured.** It cannot fire on time — a `setTimeout`
 * cannot outrun work on the same thread as the clock, so an 8 s cap fired at 9.4 s. And it
 * cannot make the wait shorter on a slow device: a phone that needs 40 s to build this scene
 * will spend 40 s on the loading screen, which is the cost of the decision and is worth
 * re-measuring on real hardware before this ships to players.
 */
export const HERO_EXPECTED_MS = 11_000;
export const BOOT_HERO_CAP_MS = 45_000;

(async () => {
  // The splash is already on screen — it is markup in `index.html`, not something rendered here
  // (see `boot-splash.ts`). All this does is take the handle and drive the bar; a missing node
  // (jsdom, `/arena.html`) answers null and every call below is optional.
  const splash = takeBootSplash();
  // Floor 0: this bar is nobody's hand-off. `BootProgress` is reused for the monotonic weighting
  // and nothing else.
  const progress = new BootProgress(BOOT_SPLASH_STAGES, 0);
  const paint = (): void => {
    splash?.setProgress(progress.overall() / 100);
    splash?.setLabel(bootStageLabel(progress.current()));
  };
  progress.onChange(paint);
  paint();

  // Before the first `await`, deliberately. The splash waits for the hero's first frame, so the
  // only thing that shortens the wait is starting the megabyte earlier: fired here it downloads
  // alongside site-info, the locale and the session instead of after all three. Only on `/` — a
  // signed-in visitor at `/` is redirected to `/play` and would have paid for a scene nobody
  // sees, which is one wasted download per session against a shorter loading screen on every
  // cold visit. The rejection is handled here as well as in `mountHero`, so a failed chunk is a
  // warning rather than an unhandled rejection during boot.
  if (location.pathname === '/') {
    void preloadLandingScene().catch(() => {
      /* `mountHero` reports it and falls back to the key art */
    });
  }

  // Fetch site-info first — drives preview-mode gating throughout the app. The locale resolves
  // alongside it: neither depends on the other, and `t()` already answers from the bundled
  // fallback catalogue, so this only decides which catalogue the first paint uses.
  await Promise.all([fetchSiteInfo(), initI18n()]);
  progress.complete('settings');
  if (isPreviewBuild()) stripBrandMeta();
  // Preview/sandbox builds never report into the game's analytics property.
  else initAnalytics();

  await bootstrapSession();
  progress.complete('session');

  const landing = render();
  progress.complete('mount');

  // On `/` the splash stays up until the hero has actually rendered a frame — the call once
  // the numbers were in: keep the live scene, hold the loader until it has rendered, and only
  // then let the visitor onto the landing.
  // Anywhere else there is nothing left to wait for; nobody arriving at `/faq` should wait on
  // three.js.
  if (landing) {
    // Nothing in that path reports a fraction — it is a chunk download, an HDRI, the map's PBR
    // textures and five rigged GLBs, none of which expose progress — so the stage creeps on a
    // timer, capped at 90%: the last tenth belongs to the frame that actually lands. The creep
    // is not smooth, and cannot be: scene construction blocks the main thread in bursts of up
    // to three seconds, and a blocked thread runs no timers either. The bar moves between them.
    const startedAt = Date.now();
    const ramp = window.setInterval(() => {
      progress.set('hero', Math.min(0.9, (Date.now() - startedAt) / HERO_EXPECTED_MS));
    }, 100);
    let capped = true;
    await Promise.race([
      landing.ready.then(() => { capped = false; }),
      new Promise<void>((resolve) => window.setTimeout(resolve, BOOT_HERO_CAP_MS)),
    ]);
    window.clearInterval(ramp);
    // Giving up means committing to it: put the rendered still in the hero *before* the page is
    // revealed, so the visitor gets one picture rather than an empty stage that a different one
    // slides into later. `mountHero` never inserts it while a scene may still arrive, precisely
    // because the two are not the same shot.
    if (capped) landing.showStill();
  }
  progress.complete('hero');
  splash?.finish();

  // Subscribed only after the first paint: `initI18n()` notifies its listeners as it settles on
  // a locale, and a subscription taken before that would fire a render before the session was
  // bootstrapped — a flash of the landing page at a signed-in player.
  onLocaleChange(remountForLocale);

  authStore.subscribe((s) => {
    const path = location.pathname;
    // Signing out from a meta route lands on the landing page; `/` itself re-renders as it.
    if (!s.user && (path === '/' || isMetaRoute(path))) navigate('/');
  });
})();

window.addEventListener('beforeunload', () => {
  teardown?.();
  stopAuthMusic();
});
